// ── PLTab.js ──────────────────────────────────────────────────────────────────
import { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import {
  C, GROUPS,
  belongsToEntity, getGroup, getOpexNames, parsePL,
  useIsMobile,
  Spinner, ErrorBanner, StatRow, PillToggle,
} from './financeUtils';
// Note: GROUPS.SASSYS_SOLO has been removed — "Sassy's Marketing" is now in GROUPS.SASSYS_OPEX

// ── Dynamic allocation: returns the amount needed to zero out LM net profit ───
// For any month >= 2026-04, calculates LM-only net profit from the maps data.
// If LM net is negative, allocation = abs(lmNet). If zero or positive, no allocation.
// entity = 'lekkermate' → shown as negative (cost going out)
// entity = 'sassys'     → shown as positive (cost coming in)
// entity = 'both'       → 0 (nets to zero at group level)
function getDynamicAlloc(monthKey, maps) {
  if (!monthKey || monthKey < '2026-04') return 0;
  const m = maps[monthKey] || {};
  const lmInc  = GROUPS.LM_INCOME.reduce((s, n) => s + (m[n] || 0), 0);
  const lmCogs = GROUPS.LM_COGS.reduce((s, n) => s + (m[n] || 0), 0);
  const lmPay  = GROUPS.LM_PAYROLL.reduce((s, n) => s + (m[n] || 0), 0);
  const lmOpex = Object.keys(m).filter(n => getGroup(n) === 'LM_OPEX').reduce((s, n) => s + (m[n] || 0), 0);
  const lmNet  = lmInc - lmCogs - lmPay - lmOpex;
  return lmNet < 0 ? Math.abs(lmNet) : 0;
}

// Aggregate dynamic alloc over a set of monthly keys
function getDynamicAllocSum(monthKeys, maps) {
  return monthKeys
    .filter(k => k >= '2026-04')
    .reduce((s, k) => s + getDynamicAlloc(k, maps), 0);
}

// ── P&L Table ─────────────────────────────────────────────────────────────────
function PLTable({ curKey, lyKey, maps, period = {}, entity, isMobile, onDrillDown, curPeriod, lyPeriod, fromDate, toDate, lyFromDate, lyToDate, accountCodes }) {
  const opexNames = getOpexNames(maps, entity);

  const sections = [
    {
      title: 'Income', isExpense: false,
      rows: [
        ...(entity !== 'sassys'     ? [{ label: 'Lekker Mate Income', names: GROUPS.LM_INCOME     }] : []),
        ...(entity !== 'lekkermate' ? [{ label: "Sassy's Revenue",    names: GROUPS.SASSYS_INCOME }] : []),
      ],
    },
    {
      title: 'Cost of Sales', isExpense: true,
      rows: [
        ...(entity !== 'sassys'     ? [{ label: 'Lekker Mate COGS',  names: GROUPS.LM_COGS     }] : []),
        ...(entity !== 'lekkermate' ? [{ label: "Sassy's Food Cost", names: GROUPS.SASSYS_COGS }] : []),
      ],
    },
    {
      title: 'Payroll', isExpense: true,
      rows: [
        ...(entity !== 'sassys'     ? [{ label: 'Lekker Mate Payroll', names: GROUPS.LM_PAYROLL     }] : []),
        ...(entity !== 'lekkermate' ? [{ label: "Sassy's Payroll",     names: GROUPS.SASSYS_PAYROLL }] : []),
      ],
    },
    {
      title: 'Operating Expenses', isExpense: true,
      rows: [
        ...opexNames.map(n => ({ label: n, names: [n], drillable: true })),
        ...(entity !== 'lekkermate' ? GROUPS.SASSYS_OPEX.filter(n => !opexNames.includes(n) && Object.values(maps).some(m => m[n])).map(n => ({ label: n, names: [n], drillable: true })) : []),
      ],
    },
    ...(entity !== 'both' ? [{
      title: 'Group Allocation', isExpense: true,
      rows: [{ label: "LM Sassy's Allocation", names: ['__LM_SASSYS_ALLOC__'], isAllocation: true }],
    }] : []),
  ];

  function getAllocAmount(key) {
    if (entity === 'both' || !key) return 0;
    if (key.match(/^\d{4}-\d{2}$/)) {
      return getDynamicAlloc(key, maps);
    }
    let monthKeys = [];
    if (key === 'YTD' && period.ytdMonths) {
      monthKeys = period.ytdMonths;
    } else if (key.match(/^\d{4}-Q(\d)$/)) {
      const [yr, qs] = key.split('-Q');
      const q = parseInt(qs), s = (q - 1) * 3 + 1;
      for (let m = s; m < s + 3; m++) monthKeys.push(`${yr}-${String(m).padStart(2, '0')}`);
    } else {
      monthKeys = Object.keys(maps).filter(k => k.match(/^\d{4}-\d{2}$/) && k >= '2026-04');
    }
    return getDynamicAllocSum(monthKeys, maps);
  }

  function allocVal(key) {
    const amount = getAllocAmount(key);
    if (amount === 0) return 0;
    if (entity === 'lekkermate') return -amount;
    if (entity === 'sassys')     return  amount;
    return 0;
  }

  function colVal(names, key) {
    if (names[0] === '__LM_SASSYS_ALLOC__') return allocVal(key);
    const m = maps[key] || {};
    return names.filter(n => belongsToEntity(n, entity)).reduce((s, n) => s + (m[n] || 0), 0);
  }
  function sectionTotal(rows, key) { return rows.reduce((s, r) => s + colVal(r.names, key), 0); }
  function netProfit(key) {
    const m = maps[key] || {};
    const inc  = [...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const cogs = [...GROUPS.LM_COGS,  ...GROUPS.SASSYS_COGS  ].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const pay  = [...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const opex = opexNames.reduce((s,n)=>s+(m[n]||0),0)
      + (entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(m[n]||0),0):0);
    return inc - cogs - pay - opex - allocVal(key);
  }

  function fmtN(n) { return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n)); }
  function varPct(cur, ly, isExpense) {
    if (!ly || ly === 0) return null;
    const pct = ((cur - ly) / Math.abs(ly)) * 100;
    const good = isExpense ? pct <= 0 : pct >= 0;
    return { pct: Math.round(pct), good };
  }

  const th = { padding: '0.55rem 0.9rem', color: C.white, fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', verticalAlign: 'bottom' };
  const tdBase = { padding: isMobile ? '0.5rem 0.7rem' : '0.55rem 0.9rem', fontSize: isMobile ? '0.8rem' : '0.83rem', whiteSpace: 'nowrap' };
  const colWidths = isMobile ? ['55%', '25%', '20%'] : ['45%', '25%', '30%'];

  function VarCell({ cur, ly, isExpense, bold }) {
    const v = varPct(cur, ly, isExpense);
    if (!v) return <td style={{ ...tdBase, textAlign: 'right', color: C.faint }}>—</td>;
    const diff = cur - ly;
    const sign = diff >= 0 ? '+' : '';
    return (
      <td style={{ ...tdBase, textAlign: 'right', fontWeight: bold ? 800 : 600, color: v.good ? C.green : C.red, whiteSpace: 'nowrap' }}>
        {isMobile
          ? `${v.pct >= 0 ? '+' : ''}${v.pct}%`
          : `${sign}${fmtN(diff)} · ${v.pct >= 0 ? '+' : ''}${v.pct}%`
        }
      </td>
    );
  }

  return (
    <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', width: '100%' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: colWidths[0] }} />
          <col style={{ width: colWidths[1] }} />
          <col style={{ width: colWidths[2] }} />
        </colgroup>
        <thead>
          <tr style={{ background: C.navy }}>
            <th style={{ ...th, textAlign: 'left' }}>Account</th>
            <th style={{ ...th, textAlign: 'right' }}>{curPeriod}</th>
            <th style={{ ...th, textAlign: 'right', color: 'rgba(255,255,255,0.6)', fontSize: '0.62rem' }}>
              vs LY {!isMobile && '(€ / %)'}
            </th>
          </tr>
        </thead>
        <>{sections.map(section => {
          const visRows = section.rows.filter(r => r.isAllocation || r.names.some(n => Object.values(maps).some(m => m[n])));
          if (!visRows.length) return null;
          return (
            <tbody key={section.title}>
              <tr>
                <td colSpan={3} style={{ padding: '0.55rem 0.9rem 0.2rem', background: C.sectionBg, fontWeight: 800, color: C.mid, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', borderTop: `2px solid ${C.border}` }}>
                  {section.title}
                </td>
              </tr>
              {visRows.map((row, ri) => {
                const cur = colVal(row.names, curKey);
                const ly  = colVal(row.names, lyKey);
                const clickable = !row.isAllocation && onDrillDown;
                return (
                  <tr key={row.label}
                    style={{ borderTop: `1px solid ${C.border}`, background: ri % 2 === 0 ? C.white : C.sectionBg }}>
                    <td
                      style={{ ...tdBase, textAlign: 'left', paddingLeft: isMobile ? '1rem' : '1.4rem',
                        color: clickable ? C.skyText : C.ink,
                        cursor: clickable ? 'pointer' : 'default',
                        textDecoration: clickable ? 'underline dotted' : 'none',
                        textUnderlineOffset: 3,
                      }}
                      onClick={clickable ? () => onDrillDown(row.label, accountCodes?.[row.label]) : undefined}
                      title={clickable ? 'Click to see monthly trend' : undefined}
                    >
                      {row.label}
                    </td>
                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 600, color: C.ink }}>{fmtN(cur)}</td>
                    <VarCell cur={cur} ly={ly} isExpense={section.isExpense} />
                  </tr>
                );
              })}
              <tr style={{ borderTop: `2px solid ${C.borderMid}`, background: C.rowAlt }}>
                <td style={{ ...tdBase, textAlign: 'left', fontWeight: 700, color: C.ink }}>Total {section.title}</td>
                <td style={{ ...tdBase, textAlign: 'right', fontWeight: 800, color: C.ink }}>{fmtN(sectionTotal(visRows, curKey))}</td>
                <VarCell cur={sectionTotal(visRows, curKey)} ly={sectionTotal(visRows, lyKey)} isExpense={section.isExpense} bold />
              </tr>
            </tbody>
          );
        })}</>
        <tfoot>
          <tr style={{ borderTop: `2px solid ${C.borderMid}`, background: C.rowAlt }}>
            <td style={{ ...tdBase, fontWeight: 800, color: C.ink, fontSize: isMobile ? '0.85rem' : '0.9rem' }}>Net Profit</td>
            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 800, fontSize: isMobile ? '0.85rem' : '0.9rem', color: netProfit(curKey) >= 0 ? C.green : C.red }}>
              {fmtN(netProfit(curKey))}
            </td>
            <VarCell cur={netProfit(curKey)} ly={netProfit(lyKey)} isExpense={false} bold />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── P&L Drill Modal ───────────────────────────────────────────────────────────
function PLDrillModal({ accountName, currentYear, onClose }) {
  const isMobile = useIsMobile();
  const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const lyYear = currentYear - 1;

  const [loading, setLoading]     = useState(true);
  const [chartData, setChartData] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      const keys = [];
      for (let m = 1; m <= 12; m++) {
        keys.push(`${currentYear}-${String(m).padStart(2,'0')}`);
        keys.push(`${lyYear}-${String(m).padStart(2,'0')}`);
      }
      const { data } = await supabase.from('xero_pl_cache').select('period_key, report_data').in('period_key', keys);
      const monthMaps = {};
      (data || []).forEach(r => { const { map } = parsePL(r.report_data); monthMaps[r.period_key] = map; });
      setChartData(MONTHS_SHORT.map((label, i) => {
        const m = i + 1;
        const tyKey = `${currentYear}-${String(m).padStart(2,'0')}`;
        const lyKey = `${lyYear}-${String(m).padStart(2,'0')}`;
        return { label, ty: monthMaps[tyKey]?.[accountName] || null, ly: monthMaps[lyKey]?.[accountName] || null };
      }));
      setLoading(false);
    }
    load();
  }, [accountName, currentYear]); // eslint-disable-line

  const tyTotal = chartData.reduce((s,d) => s + (d.ty || 0), 0);
  const lyTotal = chartData.reduce((s,d) => s + (d.ly || 0), 0);
  const diff    = tyTotal - lyTotal;
  const diffPct = lyTotal ? Math.round((diff / Math.abs(lyTotal)) * 100) : null;
  const fmtN    = (v) => new Intl.NumberFormat('nl-NL',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(Math.round(v));

  const allVals  = chartData.flatMap(d => [d.ty, d.ly]).filter(v => v !== null);
  const yMin     = allVals.length ? Math.min(...allVals) : 0;
  const yMax     = allVals.length ? Math.max(...allVals) : 1000;
  const yPad     = (yMax - yMin) * 0.15 || Math.abs(yMax) * 0.15 || 500;
  const rawMax   = yMax + yPad;
  const step     = rawMax > 50000 ? 10000 : rawMax > 20000 ? 5000 : rawMax > 5000 ? 2000 : rawMax > 1000 ? 1000 : rawMax > 200 ? 50 : 10;
  const domain   = [Math.floor((yMin - yPad) / step) * step, Math.ceil(rawMax / step) * step];
  const fmtAxis  = (v) => { const a = Math.abs(v); return a >= 1000 ? `€${v < 0 ? '-' : ''}${Math.round(a/1000)}k` : `€${Math.round(v)}`; };
  const varAccent = diffPct == null ? C.mid : diffPct <= 0 ? C.green : C.amber;
  const varBg     = diffPct == null ? C.cream : diffPct <= 0 ? C.greenBg : C.amberBg;

  const CustomTooltipDrill = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: C.charcoal, color: C.white, borderRadius: 8, padding: '0.5rem 0.85rem', fontSize: '0.78rem', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
        {payload.map((p, i) => <div key={i} style={{ color: p.color, fontWeight: 600 }}>{p.name}: {fmtN(p.value)}</div>)}
      </div>
    );
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(47,58,66,0.4)', zIndex:1000, display:'flex', alignItems: isMobile?'flex-end':'center', justifyContent:'center', padding: isMobile?0:'1rem' }}
      onClick={onClose}>
      <div style={{ background:C.white, borderRadius: isMobile?'16px 16px 0 0':16, width:'100%', maxWidth:640, maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 16px 48px rgba(0,0,0,0.14)' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ padding:'1.25rem 1.25rem 1rem', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontWeight:800, fontSize:'0.95rem', color:C.ink }}>{accountName}</div>
              <div style={{ fontSize:'0.75rem', color:C.mid, marginTop:2 }}>Monthly · {currentYear} vs {lyYear}</div>
            </div>
            <button onClick={onClose} style={{ border:'none', background:C.cream, borderRadius:8, padding:'0.35rem 0.45rem', cursor:'pointer', display:'flex', alignItems:'center' }}>
              <X size={15} color={C.mid} />
            </button>
          </div>
          {!loading && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:'0.5rem', marginTop:'1rem' }}>
              {[
                { label: String(currentYear), value: fmtN(tyTotal), accent: C.skyText, bg: C.skyBg },
                { label: String(lyYear),      value: fmtN(lyTotal), accent: C.mid,     bg: C.cream },
                { label: 'vs LY', value: `${diffPct != null ? (diffPct >= 0 ? '+' : '') + diffPct + '%' : '—'}`, accent: varAccent, bg: varBg,
                  sub: diff !== 0 ? `${diff >= 0 ? '+' : ''}${fmtN(diff)}` : undefined },
              ].map(t => (
                <div key={t.label} style={{ background: t.bg, borderRadius:8, padding:'0.6rem 0.75rem', border:`1px solid ${C.border}` }}>
                  <div style={{ fontSize:'0.63rem', color:C.mid, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:3 }}>{t.label}</div>
                  <div style={{ fontSize:'0.95rem', fontWeight:800, color:t.accent }}>{t.value}</div>
                  {t.sub && <div style={{ fontSize:'0.7rem', color:C.mid, marginTop:2 }}>{t.sub}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div style={{ padding:'1rem 1.25rem 1.5rem', flex:1, minHeight:0 }}>
          {loading ? <Spinner label="Loading trend…" /> : (
            <>
              <div style={{ display:'flex', gap:'1.25rem', marginBottom:'0.75rem' }}>
                {[[C.skyText, String(currentYear), false],[C.mid, String(lyYear), true]].map(([color, label, dashed]) => (
                  <div key={label} style={{ display:'flex', alignItems:'center', gap:5 }}>
                    <svg width={20} height={10}><line x1={0} y1={5} x2={20} y2={5} stroke={color} strokeWidth={2} strokeDasharray={dashed?'5,3':undefined}/></svg>
                    <span style={{ fontSize:'0.75rem', color:C.mid, fontWeight:600 }}>{label}</span>
                  </div>
                ))}
              </div>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 240}>
                <LineChart data={chartData} margin={{ top:4, right:8, left:0, bottom:0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize:11, fill:C.mid }} axisLine={false} tickLine={false} />
                  <YAxis domain={domain} tick={{ fontSize:10, fill:C.mid }} axisLine={false} tickLine={false} tickFormatter={fmtAxis} width={52} />
                  <Tooltip content={<CustomTooltipDrill />} />
                  <Line type="monotone" dataKey="ty" name={String(currentYear)} stroke={C.skyText} strokeWidth={2.5} dot={false} connectNulls />
                  <Line type="monotone" dataKey="ly" name={String(lyYear)} stroke={C.mid} strokeWidth={1.5} strokeDasharray="5 3" dot={false} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── YTD Monthly Breakdown Table ───────────────────────────────────────────────
function YTDMonthlyTable({ maps, entity, maxYear, maxMonth }) {
  const opexNames = getOpexNames(maps, entity);
  const months  = Array.from({length: maxMonth}, (_,i) => i+1);
  const mLabels = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  function mk(m) { return `${maxYear}-${String(m).padStart(2,'0')}`; }
  function fmtN(n) { return new Intl.NumberFormat('nl-NL',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(Math.round(n)); }
  function mVal(names, key) {
    if (names[0] === '__LM_SASSYS_ALLOC__') {
      const amount = getDynamicAlloc(key, maps);
      if (entity === 'lekkermate') return -amount;
      if (entity === 'sassys')     return  amount;
      return 0;
    }
    const m=maps[key]||{}; return names.filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
  }
  function mNet(key) {
    const m=maps[key]||{};
    const inc =[...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const cogs=[...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const pay =[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
    const ox  =opexNames.reduce((s,n)=>s+(m[n]||0),0)
      +(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(m[n]||0),0):0);
    const amount = getDynamicAlloc(key, maps);
    const alloc  = entity==='lekkermate' ? -amount : entity==='sassys' ? amount : 0;
    return inc-cogs-pay-ox-alloc;
  }

  const sections = [
    { title:'Income', isExpense:false, rows:[
      ...(entity!=='sassys'?[{label:'Lekker Mate Income',names:GROUPS.LM_INCOME}]:[]),
      ...(entity!=='lekkermate'?[{label:"Sassy's Revenue",names:GROUPS.SASSYS_INCOME}]:[]),
    ], totalNames:[...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME] },
    { title:'Cost of Sales', isExpense:true, rows:[
      ...(entity!=='sassys'?[{label:'Lekker Mate COGS',names:GROUPS.LM_COGS}]:[]),
      ...(entity!=='lekkermate'?[{label:"Sassy's Food Cost",names:GROUPS.SASSYS_COGS}]:[]),
    ], totalNames:[...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS] },
    { title:'Payroll', isExpense:true, rows:[
      ...(entity!=='sassys'?[{label:'Lekker Mate Payroll',names:GROUPS.LM_PAYROLL}]:[]),
      ...(entity!=='lekkermate'?[{label:"Sassy's Payroll",names:GROUPS.SASSYS_PAYROLL}]:[]),
    ], totalNames:[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL] },
    { title:'Operating Expenses', isExpense:true, rows:[
      ...opexNames.map(n=>({label:n,names:[n]})),
      ...(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)&&Object.values(maps).some(m=>m[n])).map(n=>({label:n,names:[n]})):[]),
    ], totalNames:null },
    ...(entity !== 'both' ? [{
      title:'Group Allocation', isExpense:true,
      rows:[{label:"LM Sassy's Allocation",names:['__LM_SASSYS_ALLOC__'],isAllocation:true}],
      totalNames:null,
    }] : []),
  ];

  const thStyle = { padding:'0.5rem 0.7rem', fontSize:'0.65rem', fontWeight:700, textTransform:'uppercase', color:C.white, textAlign:'right', whiteSpace:'nowrap', background:C.navy };
  const tdStyle = (bold, color) => ({ padding:'0.45rem 0.7rem', textAlign:'right', fontWeight:bold?700:400, color:color||C.ink, whiteSpace:'nowrap', fontSize:'0.8rem' });

  return (
    <div style={{overflowX:'auto',WebkitOverflowScrolling:'touch',width:'100%'}}>
      <table style={{borderCollapse:'collapse',fontSize:'0.8rem',width:'100%',minWidth:700}}>
        <thead>
          <tr style={{background:C.navy}}>
            <th style={{...thStyle,textAlign:'left',minWidth:180,position:'sticky',left:0,background:C.navy,zIndex:2}}>Account</th>
            {months.map(m=><th key={m} style={thStyle}>{mLabels[m-1]}</th>)}
            <th style={{...thStyle,borderLeft:`2px solid rgba(255,255,255,0.2)`,color:C.yellow}}>YTD</th>
          </tr>
        </thead>
        <>{sections.map(section => {
          const visRows = section.rows.filter(r=>r.isAllocation||r.names.some(n=>months.some(m=>maps[mk(m)]?.[n])));
          if (!visRows.length) return null;
          const stickyBg = (ri) => ri%2===0 ? C.white : C.sectionBg;
          return (
            <tbody key={section.title}>
              <tr>
                <td colSpan={months.length+2} style={{padding:'0.5rem 0.7rem 0.2rem',background:C.sectionBg,fontWeight:800,color:C.mid,fontSize:'0.63rem',textTransform:'uppercase',letterSpacing:'0.08em',borderTop:`2px solid ${C.border}`,position:'sticky',left:0}}>
                  {section.title}
                </td>
              </tr>
              {visRows.map((row,ri)=>(
                <tr key={row.label} style={{borderTop:`1px solid ${C.border}`,background:stickyBg(ri)}}>
                  <td style={{padding:'0.45rem 0.7rem 0.45rem 1.2rem',color:C.ink,whiteSpace:'nowrap',fontSize:'0.8rem',position:'sticky',left:0,background:stickyBg(ri),zIndex:1}}>{row.label}</td>
                  {months.map(m=>{ const v=mVal(row.names,mk(m)); return <td key={m} style={tdStyle(false)}>{v!==0?fmtN(v):'—'}</td>; })}
                  <td style={{...tdStyle(false),borderLeft:`2px solid ${C.border}`,color:C.navy,fontWeight:600}}>
                    {fmtN(months.reduce((s,m)=>s+mVal(row.names,mk(m)),0))}
                  </td>
                </tr>
              ))}
              <tr style={{borderTop:`2px solid ${C.borderMid}`,background:C.rowAlt}}>
                <td style={{padding:'0.45rem 0.7rem',fontWeight:700,color:C.ink,position:'sticky',left:0,background:C.rowAlt,zIndex:1,fontSize:'0.8rem'}}>Total {section.title}</td>
                {months.map(m=>{
                  const v = section.totalNames ? mVal(section.totalNames,mk(m)) : visRows.reduce((s,r)=>s+mVal(r.names,mk(m)),0);
                  return <td key={m} style={{...tdStyle(true),color:C.ink}}>{fmtN(v)}</td>;
                })}
                <td style={{...tdStyle(true),color:C.navy,borderLeft:`2px solid ${C.border}`}}>
                  {fmtN(months.reduce((s,m)=>{ const v=section.totalNames?mVal(section.totalNames,mk(m)):visRows.reduce((ss,r)=>ss+mVal(r.names,mk(m)),0); return s+v; },0))}
                </td>
              </tr>
            </tbody>
          );
        })}</>
        <tfoot>
          <tr style={{borderTop:`2px solid ${C.borderMid}`,background:C.rowAlt}}>
            <td style={{padding:'0.5rem 0.7rem',fontWeight:800,color:C.ink,position:'sticky',left:0,background:C.rowAlt,zIndex:1,fontSize:'0.83rem'}}>Net Profit</td>
            {months.map(m=>{ const v=mNet(mk(m)); return <td key={m} style={{padding:'0.45rem 0.7rem',textAlign:'right',fontWeight:800,color:v>=0?C.green:C.red,whiteSpace:'nowrap',fontSize:'0.83rem'}}>{fmtN(v)}</td>; })}
            <td style={{padding:'0.45rem 0.7rem',textAlign:'right',fontWeight:800,fontSize:'0.83rem',borderLeft:`2px solid ${C.border}`,color:(()=>{const t=months.reduce((s,m)=>s+mNet(mk(m)),0);return t>=0?C.green:C.red;})()}}>
              {fmtN(months.reduce((s,m)=>s+mNet(mk(m)),0))}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// P&L TAB
// ══════════════════════════════════════════════════════════════════════════════
export default function PLTab() {
  const isMobile = useIsMobile();
  const now = new Date();

  const [view,     setView]    = useState('ytd');
  const [entity,   setEntity]  = useState('both');
  const [maps,     setMaps]    = useState({});
  const [loading,  setLoading] = useState(false);
  const [error,    setError]   = useState(null);
  const [accountCodes, setAccountCodes] = useState({});
  const [drillRow, setDrillRow] = useState(null);
  const [showMonthlyBreakdown, setShowMonthlyBreakdown] = useState(false);

  const [stepYear,  setStepYear]  = useState(now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear());
  const [stepMonth, setStepMonth] = useState(now.getMonth() === 0 ? 12 : now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const selectedQ = 'all';

  function monthKey(y, m) { return `${y}-${String(m).padStart(2, '0')}`; }
  function monthLabel(y, m) { return new Date(y, m - 1, 1).toLocaleString('en-NL', { month: 'short', year: '2-digit' }); }
  function monthLong(y, m)  { return new Date(y, m - 1, 1).toLocaleString('en-NL', { month: 'long', year: 'numeric' }); }
  function monthFrom(y, m)  { return `${monthKey(y, m)}-01`; }
  function monthTo(y, m)    { return new Date(y, m, 0).toISOString().split('T')[0]; }

  const minYear = 2025; const minMonth = 1;
  const maxYear = now.getFullYear(); const maxMonth = now.getMonth() + 1;
  const ytdMaxMonth = maxMonth === 1 ? 12 : maxMonth - 1;
  const ytdMaxYear  = maxMonth === 1 ? maxYear - 1 : maxYear;
  function canStepBack() { return stepYear > minYear || stepMonth > minMonth; }
  function canStepFwd()  { return stepYear < maxYear || stepMonth < maxMonth; }
  function stepBack() { if (!canStepBack()) return; if (stepMonth === 1) { setStepYear(y => y - 1); setStepMonth(12); } else setStepMonth(m => m - 1); }
  function stepFwd()  { if (!canStepFwd())  return; if (stepMonth === 12) { setStepYear(y => y + 1); setStepMonth(1); } else setStepMonth(m => m + 1); }

  function getPeriodKeys() {
    if (view === 'monthly') {
      const curKey = monthKey(stepYear, stepMonth);
      const lyKey  = monthKey(stepYear - 1, stepMonth);
      return { curKey, lyKey,
        curFrom: monthFrom(stepYear, stepMonth),   curTo: monthTo(stepYear, stepMonth),
        lyFrom:  monthFrom(stepYear - 1, stepMonth), lyTo: monthTo(stepYear - 1, stepMonth),
        curLabel: monthLong(stepYear, stepMonth),  lyLabel: monthLabel(stepYear - 1, stepMonth),
        keysToFetch: [curKey, lyKey],
      };
    }
    if (view === 'ytd') {
      const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const months = []; const lyMonths = [];
      for (let m = 1; m <= ytdMaxMonth; m++) { months.push(monthKey(ytdMaxYear, m)); lyMonths.push(monthKey(ytdMaxYear - 1, m)); }
      const rangeLabel = `Jan–${MONTH_SHORT[ytdMaxMonth - 1]}`;
      return { curKey: 'YTD', lyKey: 'LY-YTD',
        curFrom: monthFrom(ytdMaxYear, 1), curTo: monthTo(ytdMaxYear, ytdMaxMonth),
        lyFrom:  monthFrom(ytdMaxYear - 1, 1), lyTo: monthTo(ytdMaxYear - 1, ytdMaxMonth),
        curLabel: `YTD ${rangeLabel} ${ytdMaxYear}`, lyLabel: `YTD ${rangeLabel} ${ytdMaxYear - 1}`,
        keysToFetch: [...months, ...lyMonths], ytdMonths: months, lyMonths,
      };
    }
    if (view === 'quarterly') {
      if (selectedQ === 'all') {
        const keys = [], lyKeys = [], monthsToFetch = [];
        for (let q = 1; q <= 4; q++) {
          keys.push(`${selectedYear}-Q${q}`); lyKeys.push(`${selectedYear-1}-Q${q}`);
          const s = (q-1)*3+1;
          for (let m = s; m < s+3; m++) { monthsToFetch.push(monthKey(selectedYear,m)); monthsToFetch.push(monthKey(selectedYear-1,m)); }
        }
        return { curKey: null, lyKey: null, qtrKeys: keys, qtrLyKeys: lyKeys, keysToFetch: monthsToFetch, selectedYear, curLabel: String(selectedYear), lyLabel: String(selectedYear-1) };
      }
    }
    return { keysToFetch: [] };
  }

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    const period = getPeriodKeys();
    try {
      const { data, error: dbErr } = await supabase
        .from('xero_pl_cache')
        .select('period_key, report_data')
        .in('period_key', period.keysToFetch);
      if (dbErr) throw new Error(dbErr.message);
      if (!data?.length) throw new Error('No P&L data cached. Run a sync first.');

      const monthMaps = {};
      const accountCodesMap = {};
      data.forEach(r => {
        const { map, codes } = parsePL(r.report_data);
        monthMaps[r.period_key] = map;
        Object.assign(accountCodesMap, codes);
      });

      function aggregateMonths(keys) {
        const agg = {};
        keys.forEach(k => { Object.entries(monthMaps[k] || {}).forEach(([n,v]) => { agg[n] = (agg[n]||0)+v; }); });
        return agg;
      }

      const newMaps = {};
      if (view === 'monthly') {
        newMaps[period.curKey] = monthMaps[period.curKey] || {};
        newMaps[period.lyKey]  = monthMaps[period.lyKey]  || {};
      }
      if (view === 'ytd') {
        newMaps['YTD']    = aggregateMonths(period.ytdMonths);
        newMaps['LY-YTD'] = aggregateMonths(period.lyMonths);
        period.ytdMonths.forEach(k => { newMaps[k] = monthMaps[k] || {}; });
        period.lyMonths.forEach(k  => { newMaps[k] = monthMaps[k] || {}; });
      }
      if (view === 'quarterly') {
        if (selectedQ === 'all') {
          for (let q = 1; q <= 4; q++) {
            const s = (q-1)*3+1;
            const curMonths = [], lyMonths = [];
            for (let m = s; m < s+3; m++) { curMonths.push(monthKey(selectedYear,m)); lyMonths.push(monthKey(selectedYear-1,m)); }
            newMaps[`${selectedYear}-Q${q}`]   = aggregateMonths(curMonths);
            newMaps[`${selectedYear-1}-Q${q}`] = aggregateMonths(lyMonths);
            // store individual months so getDynamicAlloc can work per-month
            curMonths.forEach(k => { newMaps[k] = monthMaps[k] || {}; });
            lyMonths.forEach(k  => { newMaps[k] = monthMaps[k] || {}; });
          }
        } else {
          newMaps[period.curKey] = aggregateMonths(period.keysToFetch.filter(k => k.startsWith(String(selectedYear))));
          newMaps[period.lyKey]  = aggregateMonths(period.keysToFetch.filter(k => k.startsWith(String(selectedYear-1))));
        }
      }

      setMaps(newMaps);
      setAccountCodes(accountCodesMap);
    } catch(e) { setError(e.message); }
    finally { setLoading(false); }
  }, [view, stepYear, stepMonth, selectedYear, selectedQ]); // eslint-disable-line

  useEffect(() => { loadData(); }, [view, stepYear, stepMonth, selectedYear, selectedQ]); // eslint-disable-line
  useEffect(() => { setShowMonthlyBreakdown(false); }, [view]);

  const period    = getPeriodKeys();
  const opexNames = getOpexNames(maps, entity);
  const curMap    = maps[period.curKey || 'YTD'] || {};
  const lyMap     = maps[period.lyKey  || 'LY-YTD'] || {};

  function mapVal(m, names) { return names.filter(n => belongsToEntity(n, entity)).reduce((s,n) => s+(m[n]||0), 0); }
  const income  = mapVal(curMap, [...GROUPS.LM_INCOME,  ...GROUPS.SASSYS_INCOME ]);
  const payroll = mapVal(curMap, [...GROUPS.LM_PAYROLL, ...GROUPS.SASSYS_PAYROLL]);
  const opexRaw = opexNames.reduce((s,n)=>s+(curMap[n]||0),0)
    + (entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(curMap[n]||0),0):0);

  // Dynamic tile allocation: sum monthly allocs for the current period
  const tileAllocAmount = (() => {
    if (entity === 'both') return 0;
    const key = period.curKey || 'YTD';
    if (key.match(/^\d{4}-\d{2}$/)) {
      return getDynamicAlloc(key, maps);
    }
    if (key === 'YTD' && period.ytdMonths) {
      return getDynamicAllocSum(period.ytdMonths, maps);
    }
    if (key.match(/^\d{4}-Q(\d)$/)) {
      const [yr, qs] = key.split('-Q');
      const q = parseInt(qs), s = (q - 1) * 3 + 1;
      const monthKeys = [];
      for (let m = s; m < s + 3; m++) monthKeys.push(`${yr}-${String(m).padStart(2, '0')}`);
      return getDynamicAllocSum(monthKeys, maps);
    }
    return 0;
  })();

  const tileAlloc = entity==='lekkermate' ? -tileAllocAmount : entity==='sassys' ? tileAllocAmount : 0;
  const opex = opexRaw + tileAlloc;
  const net  = income - mapVal(curMap,[...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS]) - payroll - opex;
  const lyIncome = mapVal(lyMap, [...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME]);
  const lyNet    = mapVal(lyMap,[...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME]) - mapVal(lyMap,[...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS]) - mapVal(lyMap,[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL]) - (opexNames.reduce((s,n)=>s+(lyMap[n]||0),0)+(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(lyMap[n]||0),0):0));

  function tileVar(cur, ly, isExpense) {
    if (!ly) return null;
    const pct = ((cur - ly) / Math.abs(ly)) * 100;
    return { pct: Math.round(pct), good: isExpense ? pct <= 0 : pct >= 0 };
  }

  function QtrAllTable() {
    const qs = [1,2,3,4];
    function qVal(names, year, q) { const m = maps[`${year}-Q${q}`] || {}; return names.filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0); }
    function qNet(year, q) {
      const s = (q - 1) * 3 + 1;
      const monthKeys = [];
      for (let m = s; m < s + 3; m++) monthKeys.push(`${year}-${String(m).padStart(2, '0')}`);
      const m = maps[`${year}-Q${q}`] || {};
      const inc  = [...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
      const cogs = [...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
      const pay  = [...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL].filter(n=>belongsToEntity(n,entity)).reduce((s,n)=>s+(m[n]||0),0);
      const ox   = opexNames.reduce((s,n)=>s+(m[n]||0),0)+(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(m[n]||0),0):0);
      const allocAmount = getDynamicAllocSum(monthKeys, maps);
      const alloc = entity==='lekkermate' ? -allocAmount : entity==='sassys' ? allocAmount : 0;
      return inc-cogs-pay-ox-alloc;
    }
    function fmtN(n) { return new Intl.NumberFormat('nl-NL',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(Math.round(n)); }
    function varPctCell(cur, ly, isExpense, bold, bordered) {
      const borderStyle = bordered ? {borderLeft:'1px solid '+C.border} : {};
      if (!ly) return <td style={{textAlign:'right',padding:'0.5rem 0.7rem',fontSize:'0.8rem',color:C.faint,...borderStyle}}>—</td>;
      const pct = Math.round(((cur-ly)/Math.abs(ly))*100);
      const good = isExpense ? pct<=0 : pct>=0;
      const diff = cur - ly; const sign = diff >= 0 ? '+' : '';
      return <td style={{textAlign:'right',padding:'0.5rem 0.7rem',fontSize:'0.8rem',fontWeight:bold?800:600,color:good?C.green:C.red,whiteSpace:'nowrap',lineHeight:1.3,...borderStyle}}>
        {sign}{fmtN(diff)}<br/><span style={{fontSize:'0.68rem',opacity:0.85}}>{pct>=0?'+':''}{pct}%</span>
      </td>;
    }
    const th2 = {padding:'0.5rem 0.7rem',fontSize:'0.65rem',fontWeight:700,textTransform:'uppercase',letterSpacing:'0.05em',color:C.white,whiteSpace:'nowrap',textAlign:'right'};
    const sections2 = [
      { title:'Income',  names:[...GROUPS.LM_INCOME,...GROUPS.SASSYS_INCOME],   isExpense:false },
      { title:'Payroll', names:[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL], isExpense:true  },
      { title:'Opex',    names:[...opexNames,...(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)):[])], isExpense:true },
    ];
    return (
      <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.82rem'}}>
          <thead>
            <tr style={{background:C.navy}}>
              <th style={{...th2,textAlign:'left',minWidth:180}}>Account</th>
              {qs.map(q=>[
                <th key={q} style={th2}>Q{q} {selectedYear}</th>,
                <th key={`vs${q}`} style={{...th2,color:'rgba(255,255,255,0.45)',fontSize:'0.6rem',borderLeft:'1px solid rgba(255,255,255,0.08)'}}>vs LY</th>
              ])}
            </tr>
          </thead>
          <tbody>
            {sections2.map(s=>(
              <tr key={s.title} style={{borderTop:`2px solid ${C.border}`,background:C.rowAlt}}>
                <td style={{padding:'0.5rem 0.9rem',fontWeight:700,color:C.ink}}>{s.title}</td>
                {qs.map(q=>[
                  <td key={q} style={{padding:'0.5rem 0.7rem',textAlign:'right',fontWeight:700,color:C.ink}}>{fmtN(qVal(s.names,selectedYear,q))}</td>,
                  varPctCell(qVal(s.names,selectedYear,q),qVal(s.names,selectedYear-1,q),s.isExpense,false,true)
                ])}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{borderTop:`2px solid ${C.borderMid}`,background:C.rowAlt}}>
              <td style={{padding:'0.5rem 0.9rem',fontWeight:800,color:C.ink}}>Net Profit</td>
              {qs.map(q=>[
                <td key={q} style={{padding:'0.5rem 0.7rem',textAlign:'right',fontWeight:800,color:qNet(selectedYear,q)>=0?C.green:C.red}}>{fmtN(qNet(selectedYear,q))}</td>,
                varPctCell(qNet(selectedYear,q),qNet(selectedYear-1,q),false,true,true)
              ])}
            </tr>
          </tfoot>
        </table>
      </div>
    );
  }

  const viewOptions = isMobile
    ? [{ key: 'monthly', label: 'Month' }, { key: 'ytd', label: 'YTD' }]
    : [{ key: 'monthly', label: 'Monthly' }, { key: 'ytd', label: 'YTD' }, { key: 'quarterly', label: 'Quarterly' }];
  const yearOptions = [now.getFullYear(), now.getFullYear() - 1];

  return (
    <div>
      {drillRow && (
        <PLDrillModal
          accountName={drillRow.label}
          currentYear={view === 'monthly' ? stepYear : selectedYear}
          onClose={() => setDrillRow(null)}
        />
      )}

      <div style={{ display: 'flex', gap: '0.6rem', marginBottom: '1.1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <PillToggle options={viewOptions} value={view} onChange={v => setView(v)} />
        {view === 'monthly' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '0.25rem 0.5rem' }}>
            <button onClick={stepBack} disabled={!canStepBack()} style={{ border: 'none', background: 'none', cursor: canStepBack() ? 'pointer' : 'default', color: canStepBack() ? C.ink : C.faint, fontSize: '1rem', padding: '0 0.25rem', lineHeight: 1 }}>‹</button>
            <span style={{ fontSize: '0.82rem', fontWeight: 700, color: C.ink, minWidth: 90, textAlign: 'center' }}>{monthLong(stepYear, stepMonth)}</span>
            <button onClick={stepFwd} disabled={!canStepFwd()} style={{ border: 'none', background: 'none', cursor: canStepFwd() ? 'pointer' : 'default', color: canStepFwd() ? C.ink : C.faint, fontSize: '1rem', padding: '0 0.25rem', lineHeight: 1 }}>›</button>
          </div>
        )}
        {view === 'quarterly' && !isMobile && (
          <select value={selectedYear} onChange={e => setSelectedYear(Number(e.target.value))}
            style={{ padding: '0.38rem 0.75rem', borderRadius: 8, border: `1.5px solid ${C.border}`, fontSize: '0.82rem', background: C.white, color: C.ink, fontWeight: 600, cursor: 'pointer' }}>
            {yearOptions.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        )}
        <div style={{ marginLeft: isMobile ? 0 : 'auto' }}>
          <PillToggle options={[{key:'both',label:'Both'},{key:'lekkermate',label:'Lekker Mate'},{key:'sassys',label:"Sassy's"}]} value={entity} onChange={setEntity} accent={C.green} />
        </div>
      </div>

      {loading && <Spinner label="Loading P&L…" />}
      {error   && <ErrorBanner message={error} />}

      {!loading && Object.keys(maps).length > 0 && (
        <>
          {(() => {
            const cogs   = mapVal(curMap, [...GROUPS.LM_COGS, ...GROUPS.SASSYS_COGS]);
            const lyCogs = mapVal(lyMap,  [...GROUPS.LM_COGS, ...GROUPS.SASSYS_COGS]);
            const fmtT   = n => new Intl.NumberFormat('nl-NL',{style:'currency',currency:'EUR',maximumFractionDigits:0}).format(Math.round(n));
            const opexRawTile = opexNames.reduce((s,n)=>s+(curMap[n]||0),0)
              +(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(curMap[n]||0),0):0);
            const lyOpexRaw   = opexNames.reduce((s,n)=>s+(lyMap[n]||0),0)
              +(entity!=='lekkermate'?GROUPS.SASSYS_OPEX.filter(n=>!opexNames.includes(n)).reduce((s,n)=>s+(lyMap[n]||0),0):0);
            const netTile = entity==='lekkermate' ? 0
                          : entity==='sassys'     ? income - mapVal(curMap,[...GROUPS.LM_COGS,...GROUPS.SASSYS_COGS]) - payroll - opexRawTile - tileAllocAmount
                          : net;
            const netSub = entity==='lekkermate' && tileAllocAmount > 0
              ? `Sassy's allocation ${fmtT(tileAllocAmount)}`
              : entity==='sassys' && tileAllocAmount > 0
              ? `${(() => { const v = tileVar(netTile, lyNet, false); return v ? `${v.pct>=0?'+':''}${v.pct}% vs LY · ` : ''; })()}incl. allocation -${fmtT(tileAllocAmount)}`
              : (() => { const v = tileVar(netTile, lyNet, false); return v ? `${v.pct>=0?'+':''}${v.pct}% vs LY` : undefined; })();
            return isMobile ? (
              <StatRow cards={[
                { label: 'Income',   value: fmtT(income),   accent: C.green,
                  sub: (() => { const v = tileVar(income, lyIncome, false); return v ? `${v.pct>=0?'+':''}${v.pct}% vs LY` : undefined; })() },
                { label: 'Expenses', value: fmtT(cogs + payroll + opexRawTile), accent: C.amber,
                  sub: (() => { const lyExp = lyCogs + mapVal(lyMap,[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL]) + lyOpexRaw; const v = tileVar(cogs+payroll+opexRawTile, lyExp, true); return v?`${v.pct>=0?'+':''}${v.pct}% vs LY`:undefined; })() },
                { label: 'Net Profit', value: fmtT(netTile), accent: netTile >= 0 ? C.green : C.red, sub: netSub },
              ]} />
            ) : (
              <StatRow cards={[
                { label: 'Income',     value: fmtT(income),      accent: C.green,
                  sub: (() => { const v = tileVar(income, lyIncome, false); return v ? `${v.pct>=0?'+':''}${v.pct}% vs LY` : undefined; })() },
                { label: 'Payroll',    value: fmtT(payroll),     accent: C.amber,
                  sub: (() => { const lyP = mapVal(lyMap,[...GROUPS.LM_PAYROLL,...GROUPS.SASSYS_PAYROLL]); const v = tileVar(payroll,lyP,true); return v?`${v.pct>=0?'+':''}${v.pct}% vs LY`:undefined; })() },
                { label: 'COGS',       value: fmtT(cogs),        accent: C.amber,
                  sub: (() => { const v = tileVar(cogs, lyCogs, true); return v ? `${v.pct>=0?'+':''}${v.pct}% vs LY` : undefined; })() },
                { label: 'Opex',       value: fmtT(opexRawTile), accent: C.amber,
                  sub: (() => { const v = tileVar(opexRawTile, lyOpexRaw, true); return v?`${v.pct>=0?'+':''}${v.pct}% vs LY`:undefined; })() },
                { label: 'Net Profit', value: fmtT(netTile),     accent: netTile >= 0 ? C.green : C.red, sub: netSub },
              ]} />
            );
          })()}

          {view === 'quarterly' && !isMobile && (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <QtrAllTable />
            </div>
          )}

          {!(view === 'quarterly' && !isMobile) &&
           !(view === 'ytd' && isMobile && showMonthlyBreakdown) && (
            <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden' }}>
              <PLTable
                curKey={period.curKey || 'YTD'}
                lyKey={period.lyKey || 'LY-YTD'}
                maps={maps}
                period={period}
                entity={entity}
                isMobile={isMobile}
                onDrillDown={(label, code) => setDrillRow({ label, code })}
                curPeriod={period.curLabel}
                lyPeriod={period.lyLabel}
                fromDate={period.curFrom}
                toDate={period.curTo}
                lyFromDate={period.lyFrom}
                lyToDate={period.lyTo}
                accountCodes={accountCodes}
              />
            </div>
          )}

          {view === 'ytd' && (
            <>
              {isMobile && (
                <button onClick={() => setShowMonthlyBreakdown(v => !v)}
                  style={{ display: 'block', width: '100%', marginTop: '0.75rem', padding: '0.65rem', borderRadius: 10, border: `1.5px solid ${C.border}`, background: C.white, color: C.navy, fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer' }}>
                  {showMonthlyBreakdown ? '← Back to YTD Total' : '↓ View Month by Month'}
                </button>
              )}
              {(!isMobile || showMonthlyBreakdown) && (
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: 12, overflow: 'hidden', marginTop: '0.75rem' }}>
                  <YTDMonthlyTable maps={maps} entity={entity} maxYear={ytdMaxYear} maxMonth={ytdMaxMonth} />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}